import React from 'react';
import Link from 'next/link';

export default function Footer() {
  return (
    <footer className="bg-[hsl(0,0%,9%)] text-white">
      <div className="container mx-auto container-padding section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <Link href="/" className="inline-block mb-6">
              <span className="font-heading font-bold text-2xl tracking-tight">
                <span className="font-bold text-white">NeXSYS</span>
              </span>
              <span className="text-xs font-medium tracking-widest uppercase text-white/70 ml-1">
                Group
              </span>
            </Link>
            <p className="text-white/70 mb-6 max-w-sm leading-relaxed">
              Building the framework for the future of construction. A revolutionary platform
              supporting multiple construction modalities for smarter, faster builds.
            </p>
            <div className="space-y-3">
              <a
                href="mailto:hello@nexsys.group"
                className="flex items-center gap-3 text-white/70 hover:text-white transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4"
                >
                  <rect width="20" height="16" x="2" y="4" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
                <span>hello@nexsys.group</span>
              </a>
              <div className="flex items-center gap-3 text-white/70">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-4 w-4 flex-shrink-0"
                >
                  <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                <span>Planet Earth</span>
              </div>
            </div>
          </div>

          {/* Company links */}
          <div>
            <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4">
              Company
            </h4>
            <ul className="space-y-3">
              {[
                { label: 'About Us', href: '/about' },
                { label: 'Platform', href: '/platform' },
                { label: 'Benefits', href: '/benefits' },
                { label: 'News', href: '/news' },
                { label: 'Contact', href: '/contact' },
              ]?.map((item) => (
                <li key={item?.label}>
                  <Link
                    href={item?.href}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {item?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Partners & Products */}
          <div>
            <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4">
              Partners
            </h4>
            <ul className="space-y-3">
              {[
                { label: 'Supply Chain Partners', href: '/partners/supply-chain' },
                { label: 'Channel Partners', href: '/partners/channel' },
              ]?.map((item) => (
                <li key={item?.label}>
                  <Link
                    href={item?.href}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {item?.label}
                  </Link>
                </li>
              ))}
            </ul>

            <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4 mt-8">
              Products
            </h4>
            <ul className="space-y-3">
              {[
                { label: 'All Products', href: '/products' },
                { label: 'Product Pipeline', href: '/pipeline' },
              ]?.map((item) => (
                <li key={item?.label}>
                  <Link
                    href={item?.href}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {item?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal & Social */}
          <div>
            <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4">
              Legal
            </h4>
            <ul className="space-y-3">
              {[
                { label: 'Privacy Policy', href: '/policies#privacy' },
                { label: 'Terms of Service', href: '/policies#terms' },
                { label: 'Cookie Policy', href: '/policies#cookies' },
              ]?.map((item) => (
                <li key={item?.label}>
                  <Link
                    href={item?.href}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {item?.label}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4">
                Follow Us
              </h4>
              <div className="flex gap-3">
                <a
                  href="https://www.linkedin.com/company/nexsysmodular.com/?viewAsMember=true"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#0A66C2] transition-colors"
                  aria-label="LinkedIn"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4 group-hover:text-white transition-colors"
                  >
                    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                    <rect width="4" height="12" x="2" y="9" />
                    <circle cx="4" cy="4" r="2" />
                  </svg>
                </a>
                <a
                  href="https://www.youtube.com/@NeXSYSGroup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group h-10 w-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#FF0000] transition-colors"
                  aria-label="YouTube"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-4 w-4 group-hover:text-white transition-colors"
                  >
                    <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
                    <path d="m10 15 5-3-5-3z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/50 text-sm">
              © 2026 <span className="font-bold text-white/50">NeXSYS</span> Group. All rights
              reserved.
            </p>
            <p className="text-white/50 text-sm">Transforming Construction Through Innovation</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
